// import Cookies from 'js-cookie';
import { DateTime, Settings } from 'luxon';
import i18next from 'i18next';
import { reaction } from 'mobx';
import { flow, types } from 'mobx-state-tree';

import { getNews } from '@common/api/news';
import { getProfile } from '@common/api/profile';
import { getStudentIndivPrograms, getStudentSimplePrograms } from '@common/api/studentPrograms';
import { checkRole } from '@common/components/RBAC';
import AuthModel from '@common/models/Auth';
import { IGroupModel } from '@common/models/Groups/Group';
import NewsItem, { INewsItemModel } from '@common/models/News/NewsItem';
import ProfileModel from '@common/models/Profile/Profile';
import { getCurrentServiceId } from '@common/models/Services/storage';
import { getCurrentStudentId, saveCurrentStudentId } from '@common/models/Users/Students/storage';
import StoreInterface from '@common/models/StoreInterface';
import StudentSimpleProgram from '@common/models/StudentPrograms/StudentSimpleProgram';
import { IStudentModel } from '@common/models/Users/Students/Student';

import PushNotifications from './models/PushNotifications';
import Theme from './models/Theme';

const Store = types
	.compose(
		StoreInterface,
		types.model({
			auth: types.optional(AuthModel, {}),
			theme: types.optional(Theme, JSON.parse(localStorage.getItem('theme') || '{}')),
			push: types.optional(PushNotifications, {}),
			studentPrograms: types.optional(types.array(types.late(() => StudentSimpleProgram)), []),
			indivPrograms: types.optional(types.array(types.late(() => StudentSimpleProgram)), []),
			profile: types.optional(ProfileModel, {}),
			hasSeenTutorial: types.optional(types.boolean, false),
			news: types.optional(types.array(types.late(() => NewsItem)), []),
			visitedNews: types.optional(
				types.map(types.boolean),
				JSON.parse(localStorage.getItem('visitedNews') || '{}'),
			),
		}),
	)
	.named('Store')
	.volatile(
		(): {
			studentProgramsLoading: boolean;
			indivStudentProgramsLoading: boolean;
			isNewsLoading: boolean;
			currentStudentId: number | null;
		} => ({
			studentProgramsLoading: false,
			indivStudentProgramsLoading: false,
			isNewsLoading: false,
			currentStudentId: null,
		}),
	)
	.views((self) => ({
		get student(): IStudentModel {
			return checkRole('Parent')
				? self.profile.parent?.students.find((student: any) => student.id === self.currentStudentId)
				: self.profile.student;
		},
		get tutorialUrl() {
			if (!self.services?.current?.id) return '';

			if (self.services.current.tutorial_url) return self.services.current.tutorial_url;

			const videoUrlArray: Record<number, string> = {
				1: '',
				2: 'https://storage.campus.key.study/tutorial/keystudy_lyceum_tutorial.mp4',
				3: 'https://storage.campus.key.study/tutorial/tis_tutorial.mp4',
			};

			return videoUrlArray?.[self.services.current.id];
		},
		get hasUnreadNews() {
			return self.news.some((newsItem) => !self.visitedNews.has(newsItem.id));
		},
		get sortedNews() {
			return [...self.news]?.sort((a: INewsItemModel, b: INewsItemModel) => {
				const dateA = DateTime.fromISO(a.created_at);
				const dateB = DateTime.fromISO(b.created_at);

				if (dateA > dateB) return -1;
				if (dateA < dateB) return 1;

				return 0;
			});
		},
		get allPrograms() {
			return [...self.studentPrograms, ...self.indivPrograms];
		},
	}))
	.views((self) => ({
		get isSelfStudentView(): boolean {
			return !self.auth.isMentorView && !!checkRole('Student');
		},
	}))
	.actions((self) => ({
		fetchNews: flow(function* () {
			if (!self.services.current?.id) return;
			self.isNewsLoading = true;

			try {
				self.news = yield getNews({
					limit: 99,
					group_ids: self.student.groups.map((group: IGroupModel) => group.id).join(','),
					service_id: self.services.current.id,
				});
			} catch (e) {
				console.error(e);
			} finally {
				self.isNewsLoading = false;
			}
		}),
		fetchStudentPrograms: flow(function* (query?: any) {
			if (!self.services.current?.id) return;
			try {
				self.studentProgramsLoading = true;
				self.studentPrograms = yield getStudentSimplePrograms({
					...query,
					service_id: self.services.current.id,
					limit: 100,
				});
			} catch (e) {
				console.error(e);
			} finally {
				self.studentProgramsLoading = false;
			}
		}),
		fetchStudentNotification: flow(function* (query?: any) {
			if (!self.services.current?.id) return;
			try {
				self.notifications.setPage(1);
				yield self.notifications.fetch({ ...query });
			} catch (e) {
				console.error(e);
			}
		}),
		fetchIndivPrograms: flow(function* (query?: any) {
			if (!self.services.current?.id) return;
			try {
				self.indivStudentProgramsLoading = true;
				self.indivPrograms = yield getStudentIndivPrograms({
					...query,
					service_id: self.services.current.id,
					page: 1,
					limit: 100,
				});
			} catch (e) {
				console.error(e);
			} finally {
				self.indivStudentProgramsLoading = false;
			}
		}),
		selectStudent(currentStudentId: number) {
			self.currentStudentId = currentStudentId;
			saveCurrentStudentId(currentStudentId);
		},
	}))
	.actions((self) => ({
		markNewsAsVisited(newsId: number) {
			self.visitedNews.set(newsId, true);
		},
		closeTutorialModal() {
			self.hasSeenTutorial = true;
			localStorage.setItem('hasSeenTutorial', 'true');
		},
		fetch: flow(function* () {
			try {
				self.loading = true;
				yield self._fetch();

				self.profile = yield getProfile();
				const profileLang = self.profile?.lang?.toLocaleLowerCase();
				if (profileLang && i18next.language !== profileLang) {
					i18next.changeLanguage(profileLang);
					Settings.defaultLocale = profileLang;
				}

				if (self.profile.services.length) {
					const storedServiceId = getCurrentServiceId();
					const storedService = self.profile.services.find(
						(service) => service.id === storedServiceId,
					);

					if (storedService?.id) {
						self.services.select(storedService.id);
					} else {
						self.services.select(self.profile.services[0].id);
					}
				}

				const isParent: boolean = checkRole('Parent');
				if (isParent) {
					// Parent
					const availableStudents = (self.profile.parent?.students ?? []) as IStudentModel[];
					const storedStudentId = getCurrentStudentId();
					const storedStudent = availableStudents.find(
						(student: any) => student.id === storedStudentId,
					);
					const currentStudent = availableStudents.find(
						(student: any) => student.id === self.currentStudentId,
					);
					const targetStudentId =
						storedStudent?.id || currentStudent?.id || availableStudents[0]?.id;

					if (targetStudentId && targetStudentId !== self.currentStudentId) {
						self.selectStudent(targetStudentId);
					}
				} else {
					// Student
					self.selectStudent(self.profile.student?.id);
				}
				self.notifications.fetch({ service_id: self.services.current.id || undefined });
				self.chat.fetch();
				self.studentPrograms = yield getStudentSimplePrograms({
					student_id: self.currentStudentId,
					service_id: self.services.current.id,
					limit: 100,
				});

				self.indivPrograms = yield getStudentIndivPrograms({
					student_id: self.currentStudentId,
					service_id: self.services.current.id,
					page: 1,
					limit: 100,
				});
				yield self.fetchNews();
				reaction(
					() => [self.services.current, self.currentStudentId],
					() => {
						self.fetchStudentNotification({
							service_id: self.services.current.id,
						});
						self.fetchStudentPrograms({
							student_id: self.currentStudentId,
							service_id: self.services.current.id,
						});
						self.fetchIndivPrograms({
							student_id: self.currentStudentId,
							service_id: self.services.current.id,
						});
						self.fetchNews();
					},
				);
				reaction(
					() => [self.profile.lang],
					() => {
						self.fetchStudentNotification({
							service_id: self.services.current.id,
						});
					},
				);
			} finally {
				self.loading = false;
			}
		}),
	}))
	.actions((self) => ({
		afterCreate() {
			const localStorageValue = localStorage.getItem('hasSeenTutorial');
			self.hasSeenTutorial = !!localStorageValue;

			reaction(
				() => self.visitedNews.size,
				() => {
					localStorage.setItem('visitedNews', JSON.stringify(self.visitedNews));
				},
			);
		},
	}));

export default Store.create();
